/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {
  ChangeEvent,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import {withTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {Divider} from '@material-ui/core';
import Container from '@material-ui/core/Container';
import {makeStyles, useTheme} from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import isNumber from 'lodash.isnumber';
// eslint-disable-next-line no-restricted-imports
import {useRouter} from 'next/compat/router';
import {SnackbarProvider} from 'notistack';
import {useAuthLogin} from '@ion/applications/src/authentication/useAuthLogin';
import {LanguageChangePrompt} from '@ion/applications/src/components/prompts/LanguageChangePrompt';
import {useShowProfile} from '@ion/applications/src/hooks/useShowProfile';
import styles from '@ion/applications/src/layout/Layout.module.scss';
import LayoutContent, {LayoutContentProps} from '@ion/applications/src/layout/LayoutContent';
import useLayoutStyle from '@ion/applications/src/layout/useLayoutStyle';
import {OfflineNotification} from '@ion/applications/src/prompts/OfflineNotification';
import {useQueryParamProvider} from '@ion/applications/src/query/useQueryParamProvider';
import {useExpandCollapse} from '@ion/dls/src/components/navigation/useExpandCollapse';
import {Option} from '@ion/dls/src/components/search/AutocompleteSearch';
import SearchOverlay from '@ion/dls/src/components/search/SearchOverlay';
import {SearchPanelHeader} from '@ion/dls/src/components/sidepanel/SearchPanelHeader';
import {enableIonSuitePhoneFooter} from '@ion/dls/src/editors/editorStyles';
import {useIsDesktop} from '@ion/dls/src/hooks/useMedia';
import Render from '@ion/dls/src/render/Render';
import {applyTranslations} from '@ion/dls/src/theme/resellers/Resellers';
import {dlsColor, dlsZIndex} from '@ion/dls/src/theme/theme';
import useThemeContext from '@ion/dls/src/theme/useThemeContext';
import {GridLayoutSizes} from '@ion/dls/src/types/components';
import {DLSTheme, TrackingMode} from '@ion/dls/src/types/types';
import PreLoadDashboardPage from '@/routes/analytics/PreLoadDashboardPage';
import ELDPortalContainer from '@/routes/eld/ELDPortalContainer';
import {getRoute, Route} from '@/routes/RouteDefinitions';
import PersistentHistoryMapHost from '@/routes/tracking2/map/containers/AssetHistory/PersistentHistoryMapHost';
import {
  getTrackingSearchOverlayOpen,
  setTrackingSearchOverlayOpen,
  subscribeTrackingSearchOverlayOpen
} from '../components/filter/current/store';
import TrackingFilters from '../components/filter/TrackingFilters';
import {SessionExpired} from '../components/notifications/SessionExpired';
import {ExportStatusPrompt} from '../components/prompts/ExportStatusPrompt';
import GlobalUserPrompt from '../components/prompts/GlobalUserPrompt';
import ResponsiveHeader from '../header/ResponsiveHeader';
import {useAssetListState} from '../hooks/useAssetListState';
import SideNav from '../nav/SideNav';
import EditUserEditor from '../routes/admin/users/EditUserEditor';
import {useSelectAssetAndResets} from '../routes/tracking/hooks/sidesheet/useSelectAssetAndResets';
import PersistentTrackingMapHost from '../routes/tracking2/map/PersistentTrackingMapHost';
import {handleQueryParams} from '../store/actions/initialization';
import {login, loginSSO} from '../store/actions/login';
import {setNavCollapsedStatus} from '../store/actions/nav';
import {
  clearSelectedTrackingSearchResult,
  search,
  setSelectedTrackingSearchResult,
  toggleMobileFilters
} from '../store/actions/tracking';
import {showEditUserSelector} from '../store/selectors/admin-users-selectors';
import {showExportSelector} from '../store/selectors/exporter-selectors';
import {initializationCompleteSelector} from '../store/selectors/initialization-selectors';
import {
  hasIonSuiteSelector,
  isAuthenticatedSelector
} from '../store/selectors/login-selectors';
import {layoutOffsetHeightSelector} from '../store/selectors/nav-selectors';
import {
  legacySearchHistorySelector,
  searchHistorySelector,
  searchLoadingSelector,
  searchResultsSelector,
  trackingModeSelector
} from '../store/selectors/tracking-selectors';

// the iOn Suite's editor dialogs keep Cancel right next to Done on phones ...
enableIonSuitePhoneFooter();

// the side nav's tracking search only searches from this many characters ...
const MIN_TRACKING_SEARCH_LENGTH = 3;

const getTrackingSearchOptionKey = (option: Option) => (
  `${option.type || 'search'}-${option.displayName || option.name || option.text || ''}-${option.timestamp || ''}`
);

const useStyles = makeStyles((theme: DLSTheme) => {
  return {
    notificationRoot : {
      top    : 66,
      zIndex : dlsZIndex('layoutNotificationRoot')
    },
    mobileNotificationRoot : {
      top                 : 0,
      zIndex              : dlsZIndex('layoutNotificationMobileRoot'),
      '& > *:first-child' : {
        paddingLeft  : 0,
        paddingRight : 0
      }
    },
    autocompletePopper : {
      width  : '100vw !important',
      height : '100%',
      zIndex : dlsZIndex('sideSheetTrackingContentAutocompletePopper')
    },
    filter : {
      backgroundColor : dlsColor('white'),
      width           : '-webkit-fill-available',
      height          : '93.5%',
      position        : 'absolute',
      zIndex          : dlsZIndex('trackingFilterWrapper')
    }
  };
});

/**
 * Defines the top-level application Layout which includes the Side Navigation and
 * Header Component (Top Navigation). All Pages except the Login Page are wrapped
 * by the Layout Component via the Application component (_app.tsx).
 *
 * Page components can override the default layout props by implementing a static
 * `getLayoutProps` and returning the specific overrides.
 *
 * ```typescript
 *
 *  const Page = ({tReady}: LocalizedPageProps) => (
 *    <>
 *      <PageTitle namespace={'admin-groups'} initialized={tReady} />
 *      {tReady ? <AdminGroups /> : <ContentLoader fullPage />}
 *    </>
 *  );
 *
 *  const AdminGroupsPage = withTranslation([
 *    'admin-groups'
 *  ])(Page) as PageLayoutConsumer;
 *
 *  // implement getLayoutProps to override the default grid prop value ...
 *  AdminGroupsPage.getLayoutProps = () => ({grid: true});
 *
 *  export default AdminGroupsPage;
 *
 * ```
 */
const Layout = ({children, grid = true}: LayoutContentProps) => {
  const theme                     = useTheme();
  const classes                   = useStyles();
  const route                     = useRouter();
  const dispatch                  = useDispatch();
  const showProfile               = useShowProfile();
  const showAssetsListRef         = useAssetListState();
  const isAuthenticated           = useSelector(isAuthenticatedSelector);
  const hasiOnSuite               = useSelector(hasIonSuiteSelector);
  const isInitialized             = useSelector(initializationCompleteSelector);
  const trackingSearchResults     = useSelector(searchResultsSelector);
  const trackingSearchHistory     = useSelector(searchHistorySelector);
  const legacySearchHistory       = useSelector(legacySearchHistorySelector);
  const trackingSearchLoading     = useSelector(searchLoadingSelector);
  const trackingMode              = useSelector(trackingModeSelector);
  const layoutOffsetHeight        = useSelector(layoutOffsetHeightSelector);
  const isLiveTracking            = trackingMode === TrackingMode.Live;
  const showExport                = useSelector(showExportSelector);
  const showEditUser              = useSelector(showEditUserSelector);
  const isPhone                   = useMediaQuery(theme.breakpoints.down('sm'));
  const pageInitialized           = isAuthenticated && isInitialized && hasiOnSuite;
  const {isCurrent}               = useThemeContext();
  const canShowLegacyMobileTrackingTools = isPhone && !isCurrent;

  const [showFilters, setShowFilters]               = useState(false);
  const [trackingSearchOpen, setTrackingSearchOpen] = useState(false);
  const [trackingSearchOverlayOpen, setTrackingSearchOverlayOpenState] = useState(
    getTrackingSearchOverlayOpen()
  );
  const [hiddenTrackingSearches, setHiddenTrackingSearches] = useState<string[]>([]);
  const {collapsed, onHandleCollapse}               = useExpandCollapse();
  const isDesktop                                   = useIsDesktop();
  const [,,,, resetSideSheet]                       = useSelectAssetAndResets();

  const fullScreen = useMemo(() => (
    isPhone && showFilters && route?.route === '/tracking'
  ), [showFilters, route?.route, isPhone]);

  const layoutStyle = useLayoutStyle({
    useExpandCollapse : isDesktop,
    collapsed         : collapsed,
    offsetHeight      : !fullScreen ? layoutOffsetHeight : null,
    backgroundColor   : dlsColor(isCurrent ? 'navBarGrey' : 'grey6'),
    showProfile
  });

  const trackingSearchOptions  = useMemo(() => {
    return trackingSearchResults?.consolidated ?? [];
  }, [trackingSearchResults]);

  const visibleTrackingSearchHistory = useMemo(() => (
    trackingSearchHistory?.filter((option: Option) => (
      !hiddenTrackingSearches.includes(getTrackingSearchOptionKey(option))
    )) ?? []
  ), [hiddenTrackingSearches, trackingSearchHistory]);

  // when a page is initialized, if the user has not been authenticated, we redirect
  // to the login page ...
  useAuthLogin({
    isAuthenticated,
    onLogin   : login,
    onLoginV2 : loginSSO
  });

  useEffect(() => {
    dispatch(setNavCollapsedStatus(collapsed));
  }, [collapsed]);

  useEffect(() => {
    dispatch(toggleMobileFilters(showFilters));
  }, [showFilters]);

  useEffect(() => (
    subscribeTrackingSearchOverlayOpen(() => {
      setTrackingSearchOverlayOpenState(getTrackingSearchOverlayOpen());
    })
  ), []);

  const handleFilterClick = useCallback(function() {
    setShowFilters(!showFilters);
  }, [showFilters]);

  const handleSearchChange = useCallback(function(e: ChangeEvent<{}>, value: string, reason: string) {
    if (value && reason !== 'reset') {
      dispatch(search(value));
    }
    if (reason === 'clear') {
      dispatch(clearSelectedTrackingSearchResult());
    }
  }, []);

  const onResultSelect = useCallback(function(e: ChangeEvent<{}>, value: any) {
    dispatch(setSelectedTrackingSearchResult(value, !showAssetsListRef.current));
  }, []);

  const handleTrackingSearchOverlayChange = useCallback(function(value: string) {
    if (value) {
      dispatch(search(value));
    } else {
      dispatch(clearSelectedTrackingSearchResult());
    }
  }, []);

  const handleTrackingSearchOverlayClose = useCallback(function() {
    setTrackingSearchOverlayOpen(false);
  }, []);

  const handleTrackingSearchOverlayResultSelect = useCallback(function(value: Option) {
    dispatch(setSelectedTrackingSearchResult(value, !showAssetsListRef.current));
  }, []);

  const handleTrackingRecentSearchRemove = useCallback(function(value: Option) {
    setHiddenTrackingSearches(searches => ([
      ...searches,
      getTrackingSearchOptionKey(value)
    ]));
  }, []);

  const onSearchFocus = useCallback(function() {
    setTrackingSearchOpen(true);
  }, []);

  const onSearchBlur = useCallback(function() {
    setTrackingSearchOpen(false);
  }, []);

  useQueryParamProvider(handleQueryParams, isAuthenticated);

  const gridLayoutSizes = useMemo(() => {
    if (route.route?.includes('/dashboard')) {
      return {
        md : 11,
        xs : 12
      } as GridLayoutSizes;
    }
  }, [route.route]);

  //Reducing left as part of the ticket ION-4270 to remove 1 pixel white line between left hand nav and map
  const updatedLayoutStyles = useMemo(() => {
    const left = isNumber(layoutStyle.left)
      ? layoutStyle.left - 1
      : layoutStyle.left;
    const currentDesktopOffset = isCurrent && isDesktop && layoutOffsetHeight ? layoutOffsetHeight : 0;
    return {
      ...layoutStyle,
      left,
      ...(isCurrent && isDesktop ? {
        height : currentDesktopOffset ? `calc(100vh - ${currentDesktopOffset}px)` : '100vh',
        top    : currentDesktopOffset
      } : {})
    };
  }, [isCurrent, isDesktop, layoutOffsetHeight, layoutStyle]);

  return (
    <div>
      {pageInitialized && (
        <Container maxWidth='xs'>
          <SnackbarProvider maxSnack={isPhone ? 1 : 3}
            anchorOrigin={{
              vertical   : 'top',
              horizontal : 'right'
            }}
            classes={{
              containerRoot : isPhone ? classes.mobileNotificationRoot : classes.notificationRoot
            }}>
            <PersistentTrackingMapHost />
            <PersistentHistoryMapHost />
            <PreLoadDashboardPage />
            <SessionExpired />
            <OfflineNotification />
            <LanguageChangePrompt />
            <ResponsiveHeader />
            <ELDPortalContainer collapsed={collapsed} />
            <SideNav {...{onToggleCollapse: onHandleCollapse}} />
            <Render.If condition={isCurrent && route?.route === getRoute(Route.TRACKING2)}>
              <SearchOverlay open={trackingSearchOverlayOpen}
                options={trackingSearchOptions}
                onChange={handleTrackingSearchOverlayChange}
                onClose={handleTrackingSearchOverlayClose}
                onResultSelect={handleTrackingSearchOverlayResultSelect}
                onRecentSearchRemove={handleTrackingRecentSearchRemove}
                loading={trackingSearchLoading}
                minSearchLength={MIN_TRACKING_SEARCH_LENGTH}
                recentSearches={visibleTrackingSearchHistory} />
            </Render.If>
            <div className={styles[isPhone ? 'phone' : `desktop${isCurrent ? 'Current' : ''}`]}
              id='layout-root'
              style={updatedLayoutStyles}>
              <LayoutContent initialized={pageInitialized}
                gridLayoutSizes={gridLayoutSizes}
                grid={isCurrent ? false : grid}>
                <Render.If condition={route?.route.includes('/tracking')}>
                  <>
                    <Render.If condition={canShowLegacyMobileTrackingTools}>
                      <Divider />
                      <SearchPanelHeader onFilter={handleFilterClick}
                        options={trackingSearchOptions}
                        onChange={handleSearchChange}
                        onFocus={onSearchFocus}
                        onBlur={onSearchBlur}
                        onResultSelect={onResultSelect}
                        loading={trackingSearchLoading}
                        portalClassOverride={classes.autocompletePopper}
                        recentSearches={legacySearchHistory}
                        trackingSearchOpen={trackingSearchOpen}
                        isPhoneTrackingSearch
                        isClearSearch />
                      <Divider />
                    </Render.If>
                    <Render.If condition={canShowLegacyMobileTrackingTools}>
                      <div className={classes.filter} style={{display: showFilters ? 'block' : 'none'}}>
                        <TrackingFilters closeMobileMenu={handleFilterClick}
                          resetSideSheet={resetSideSheet} />
                      </div>
                    </Render.If>
                  </>
                </Render.If>
                {children}
              </LayoutContent>
            </div>
          </SnackbarProvider>
          <GlobalUserPrompt />
          {showExport && <ExportStatusPrompt />}
          {showEditUser && <EditUserEditor />}
        </Container>
      )}
    </div>
  );
};

export default withTranslation(
  applyTranslations([
    'common',
    'nav',
    'notifications',
    'timezones',
    'grids',
    'sisense',
    'filters',
    'admin-groups'
  ]))(memo(Layout));
